/*
  ============================================================
  FILE: custom-header.js
  LOCATION: assets/custom-header.js

  Handles:
  1. Smart sticky header (hide on scroll down, show on scroll up)
  2. Mobile navigation drawer open/close
  Loaded via defer in custom-header.liquid.
  ============================================================
*/

(function () {
  'use strict';

  /* ── 1. SMART STICKY HEADER ─────────────────────────────────
     Hide when scrolling DOWN, show when scrolling UP.
     Always visible when near the top of the page.

     The announcement bar is fixed to the top of the viewport and sits directly
     above the header, so it has to come and go with it - a bar that stayed put
     while the nav slid away would leave a strip pinned over the page with
     nothing under it. Its hidden class is mirrored from the header's below
     rather than driven by its own scroll listener, so the two can never
     disagree about which way the page is moving.
  ──────────────────────────────────────────────────────────── */
  const header = document.querySelector('.cg-header');

  if (header) {
    const SCROLL_THRESHOLD = 10;  // px — ignore tiny jitter
    const TOP_OFFSET       = 80;  // px from top — always show header here

    let lastScrollY = window.scrollY;
    let ticking     = false;

    function syncAnnouncementBar() {
      /* Queried each pass rather than cached: the theme editor re-renders the
         bar's section on every setting change, which replaces the element. */
      const bar = document.querySelector('.announcement-bar');
      if (!bar) return;
      bar.classList.toggle(
        'announcement-bar--hidden',
        header.classList.contains('cg-header--hidden')
      );
    }

    function updateHeader() {
      const currentScrollY = window.scrollY;

      if (currentScrollY <= TOP_OFFSET) {
        // Near the top — always show
        header.classList.remove('cg-header--hidden');
        header.classList.add('cg-header--at-top');
      } else {
        header.classList.remove('cg-header--at-top');

        const diff = currentScrollY - lastScrollY;

        if (diff > SCROLL_THRESHOLD) {
          // Scrolling DOWN → hide
          header.classList.add('cg-header--hidden');
        } else if (diff < -SCROLL_THRESHOLD) {
          // Scrolling UP → show
          header.classList.remove('cg-header--hidden');
        }
      }

      syncAnnouncementBar();

      lastScrollY = currentScrollY;
      ticking     = false;
    }

    window.addEventListener('scroll', function () {
      if (!ticking) {
        requestAnimationFrame(updateHeader);
        ticking = true;
      }
    }, { passive: true });

    // Set initial state
    updateHeader();
  }

  /* ── 2. MEGA MENU HOVER WITH INTENT ─────────────────────────
     JS-controlled so we get proper open/close animation and
     hover intent (avoids instant flicker on fast cursor moves).
  ──────────────────────────────────────────────────────────── */
  const megaItems = document.querySelectorAll('.cg-nav__item--mega');

  megaItems.forEach(function (item) {
    const mega = item.querySelector('.cg-mega');
    if (!mega) return;

    let openTimer  = null;
    let closeTimer = null;

    function openMega() {
      clearTimeout(closeTimer);
      openTimer = setTimeout(function () {
        /* Re-measure the header's real height right before the panel opens.

           Reported by Dillon, 9 Aug 2026: after scrolling to the bottom of a
           page and back up, the mega panel opened cramped against the header
           instead of clearing it. The panel is `position: fixed; top:
           var(--ctx-header-h)` (custom-theme.css), and that variable is only
           ever written by a separate script's ResizeObserver/scroll-adjacent
           triggers — nothing in a normal scroll-up-to-top sequence is
           guaranteed to fire one of them again before the very next hover.
           Rather than chase which exact trigger is missing, force a correct
           reading at the one moment it actually matters: right before the
           panel becomes visible. Cheap - one getBoundingClientRect call - and
           it makes the panel's position self-correcting regardless of
           whatever state the rest of the page is in.
        */
        if (window.ctxMeasureHeaderHeight) window.ctxMeasureHeaderHeight();

        // Close any other open megas first
        megaItems.forEach(function (other) {
          if (other !== item) {
            const otherMega = other.querySelector('.cg-mega');
            if (otherMega) otherMega.classList.remove('is-open');
          }
        });
        mega.classList.add('is-open');
      }, 120);
    }

    function closeMega() {
      clearTimeout(openTimer);
      closeTimer = setTimeout(function () {
        mega.classList.remove('is-open');
      }, 250);
    }

    item.addEventListener('mouseenter', openMega);
    item.addEventListener('mouseleave', closeMega);
    // Keep open when cursor moves into the dropdown panel itself
    mega.addEventListener('mouseenter', function () { clearTimeout(closeTimer); });
    mega.addEventListener('mouseleave', closeMega);

    // Keyboard / focus support
    item.addEventListener('focusin', function () {
      clearTimeout(closeTimer);
      if (window.ctxMeasureHeaderHeight) window.ctxMeasureHeaderHeight();
      mega.classList.add('is-open');
    });
    item.addEventListener('focusout', function (e) {
      if (!item.contains(e.relatedTarget)) closeMega();
    });
  });

  /* ── Click a top-level item to open its panel ───────────────────────────
     Hover alone is not enough: on a touch screen there is no hover, so tapping
     "Info" simply followed its link and the customer never saw what was under it.
     A click now toggles the panel instead of navigating. */
  document.querySelectorAll('[data-mega-toggle]').forEach(function (toggle) {
    toggle.addEventListener('click', function (e) {
      const item = toggle.closest('.cg-nav__item');
      const mega = item && item.querySelector('.cg-mega');
      if (!mega) return;               // no panel - let the link work normally

      /* An item marked data-mega-nav is a real destination, not just a
         disclosure: "Shop Equipment" should land on Shop All.

         Only on a device that can hover, though. Hover already opens the panel
         there, so a click is free to navigate. On a touch screen there is no
         hover, so the tap has to keep opening the panel - otherwise the whole
         menu becomes unreachable on a phone, which is the exact bug this
         handler was written to fix in the first place. */
      const canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
      if (canHover && toggle.hasAttribute('data-mega-nav')) return;

      e.preventDefault();
      const wasOpen = mega.classList.contains('is-open');

      /* One panel at a time. */
      document.querySelectorAll('.cg-mega.is-open').forEach(function (m) {
        m.classList.remove('is-open');
      });
      document.querySelectorAll('[data-mega-toggle]').forEach(function (t) {
        t.setAttribute('aria-expanded', 'false');
      });

      if (!wasOpen) {
        // Same defensive re-measure as openMega() above, for tap/click opens.
        if (window.ctxMeasureHeaderHeight) window.ctxMeasureHeaderHeight();
        mega.classList.add('is-open');
        toggle.setAttribute('aria-expanded', 'true');
      }
    });
  });

  /* Clicking anywhere else closes an open panel. */
  document.addEventListener('click', function (e) {
    if (e.target.closest('.cg-nav__item--mega')) return;
    document.querySelectorAll('.cg-mega.is-open').forEach(function (m) {
      m.classList.remove('is-open');
    });
    document.querySelectorAll('[data-mega-toggle]').forEach(function (t) {
      t.setAttribute('aria-expanded', 'false');
    });
  });

  // Close all megas on Escape
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') {
      megaItems.forEach(function (item) {
        const mega = item.querySelector('.cg-mega');
        if (mega) mega.classList.remove('is-open');
      });
      document.querySelectorAll('[data-mega-toggle]').forEach(function (t) {
        t.setAttribute('aria-expanded', 'false');
      });
    }
  });

  /* ── 3. CART BUTTON → CART DRAWER ───────────────────────────
     The header renders its cart button with data-action="open-cart" but nothing
     was listening, so it always fell through to its /cart href. When the theme is
     set to drawer mode and a <cart-drawer> is present, open that instead. If the
     drawer is missing (or cart type is page), the href still works untouched.
  ──────────────────────────────────────────────────────────── */
  document.querySelectorAll('[data-action="open-cart"]').forEach(function (btn) {
    btn.addEventListener('click', function (e) {
      var drawer = document.querySelector('cart-drawer');
      if (!drawer || typeof drawer.open !== 'function') return; // let the link navigate
      e.preventDefault();
      drawer.open(btn);
    });
  });

  /* ── 4. MOBILE NAVIGATION DRAWER ───────────────────────────── */
  const menuBtn    = document.getElementById('cgMenuBtn');
  const navDrawer  = document.getElementById('cgNavDrawer');
  const navOverlay = document.getElementById('cgNavOverlay');
  const navClose   = document.getElementById('cgNavClose');

  if (!menuBtn || !navDrawer) return;

  function openNav() {
    navDrawer.classList.add('open');
    navDrawer.setAttribute('aria-hidden', 'false');
    navOverlay.style.display = 'block';
    requestAnimationFrame(() => navOverlay.classList.add('open'));
    menuBtn.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';

    // Trap focus inside drawer
    const firstFocusable = navDrawer.querySelector('button, a');
    if (firstFocusable) firstFocusable.focus();
  }

  function closeNav() {
    navDrawer.classList.remove('open');
    navDrawer.setAttribute('aria-hidden', 'true');
    navOverlay.classList.remove('open');
    menuBtn.setAttribute('aria-expanded', 'false');
    setTimeout(() => { navOverlay.style.display = 'none'; }, 250);
    document.body.style.overflow = '';
    menuBtn.focus();
  }

  menuBtn.addEventListener('click', openNav);
  if (navClose)   navClose.addEventListener('click', closeNav);
  if (navOverlay) navOverlay.addEventListener('click', closeNav);

  /*
    Tapping the empty space below the menu closes it.

    The overlay already had a click handler, but it could never fire: the drawer
    itself is a full-height fixed element (inset 0, height 100dvh) with a
    transparent background, and its menu cards only occupy the top third. So
    everything that LOOKS like "outside the menu" is still inside the drawer
    element, and the click landed on it rather than the overlay underneath.

    Rather than restructure the drawer's markup, treat a click that lands on the
    drawer's own padding - i.e. not on any of its cards or controls - as a click
    outside. The closest() check means links, buttons and the accordion rows
    continue to work normally.
  */
  navDrawer.addEventListener('click', function (e) {
    if (e.target !== navDrawer) return;   // landed on a child - leave it alone
    closeNav();
  });

  // Close on Escape key
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && navDrawer.classList.contains('open')) {
      closeNav();
    }
  });

})();

/* ═══════════════════════════════════════════════════════════
   Publish the header's on-screen height as --ctx-header-h

   The header is position:fixed, so anything that pins itself to the top of the
   viewport - sticky sidebars especially - has to know how much room the nav is
   taking or it ends up underneath it. That height is not a constant: the nav
   sits below the announcement bar, it grows when it wraps on narrow widths, and
   a visitor can dismiss the announcement bar entirely.

   Measured here once for the whole theme and exposed on <html>, so stylesheets
   can just say calc(var(--ctx-header-h) + 16px) instead of each guessing a
   number that is wrong at some widths.
═══════════════════════════════════════════════════════════ */
(function () {
  'use strict';

  /* Latched, never unset. A dismissed announcement bar does not come back
     without a page load, and a page load resets this to false. */
  var abDismissed = false;

  function measure() {
    var best = 0;
    document.querySelectorAll(
      'header, .section-header, #shopify-section-header, .custom-header, .site-header'
    ).forEach(function (el) {
      var cs = getComputedStyle(el);
      if (cs.position !== 'fixed' && cs.position !== 'sticky') return;
      var r = el.getBoundingClientRect();
      if (r.height < 24) return;
      if (r.bottom > best) best = r.bottom;
    });
    /* best is 0 when every candidate is off-screen, which is exactly what the
       header does on scroll down. Writing that would collapse the clearance
       padding on text-first pages and jump the page content up mid-scroll, so
       keep the last real measurement instead. */
    if (best > 0) {
      var next = Math.round(best) + 'px';
      /* Only write on change - this runs from a ResizeObserver on <body>, and a
         value that feeds back into layout would otherwise loop. */
      if (document.documentElement.style.getPropertyValue('--ctx-header-h') !== next) {
        document.documentElement.style.setProperty('--ctx-header-h', next);
      }
    }

    /*
      Also publish the announcement bar's height as --ctx-ab-h.

      The header's at-top offset used to be a hardcoded 70px chosen to clear that
      bar. Dismiss the bar and the offset stayed, so the header floated with a strip
      of page showing above it. Measuring the bar instead means the offset collapses
      with it: the stylesheet asks for calc(var(--ctx-ab-h) + a small gap), which
      lands at just the gap once the bar is gone.

      Reported as 0 when the bar is dismissed or absent, which is exactly what
      that calc needs.
    */
    var ab = document.querySelector('.announcement-bar');
    var abH = 0;
    /* Once dismissed, the bar is zero — full stop, whatever it currently
       measures. Without this, `measure()` runs while the collapse is still
       animating, reads the bar's pre-collapse height and writes it straight back
       over the zero the dismissal just set. The fix undid itself. */
    if (ab && !ab.hidden && !abDismissed) {
      /* offsetHeight, not the bounding rect. The bar now slides off the top on a
         transform when the header hides, and a transformed rect reads as gone -
         which would drop the header's offset to zero and slide it up the screen
         underneath a bar that is only away for a moment. offsetHeight ignores the
         transform and still collapses to 0 on a real dismissal, which animates
         max-height. */
      abH = ab.offsetHeight;
    }
    var abNext = Math.round(abH) + 'px';
    if (document.documentElement.style.getPropertyValue('--ctx-ab-h') !== abNext) {
      document.documentElement.style.setProperty('--ctx-ab-h', abNext);
    }
  }

  /* Exposed so the mega-menu code earlier in this file can force a fresh
     reading at the exact moment a dropdown opens — see the note beside
     openMega() above for why. */
  window.ctxMeasureHeaderHeight = measure;

  measure();
  window.addEventListener('load', measure);
  window.addEventListener('resize', measure);

  if (window.ResizeObserver) new ResizeObserver(measure).observe(document.body);

  /* WATCH THE ANNOUNCEMENT BAR ITSELF.

     Observing <body> alone was not enough. The bar is taken out of normal flow,
     so collapsing it does not change the body's size and the ResizeObserver
     above never fires — the bar vanished, `--ctx-ab-h` stayed at its old value,
     and the header hung there with a strip of dead page above it. That is the
     "nav doesn't collapse into its place" Dillon reported across the site.

     Three listeners because the dismissal happens in three observable steps and
     any one of them can be the last: the element resizes as max-height animates,
     the transition ends, and `hidden` is set afterwards. Cheap, and it means the
     header follows the bar however the bar chooses to leave. */
  var abEl = document.querySelector('.announcement-bar');
  if (abEl) {
    if (window.ResizeObserver) new ResizeObserver(measure).observe(abEl);
    if (window.MutationObserver) {
      new MutationObserver(measure).observe(abEl, {
        attributes: true,
        attributeFilter: ['hidden', 'style', 'class']
      });
    }
    abEl.addEventListener('transitionend', measure);
  }

  /* The announcement bar tells us directly when it is dismissed, which is the
     only signal that does not depend on an animation completing. */
  document.addEventListener('ctx:announcement-dismissed', function () {
    abDismissed = true;
    measure();
    /* Again after the collapse animation, so --ctx-header-h picks up the
       header's new bottom edge rather than the pre-collapse one. */
    setTimeout(measure, 60);
    setTimeout(measure, 400);
  });

  /* A late safety net: some dismissals finish after this script has stopped
     listening, and a header sitting 52px too low is very visible. */
  setTimeout(measure, 800);
  setTimeout(measure, 2000);
})();
/* ── Cart badge ───────────────────────────────────────────────────────────
   Keeps the number on the cart icon honest after an AJAX add.

   The badge is now always in the DOM (hidden by a class when the cart is
   empty), so this only has to find it and set a number. Previously it was not
   rendered at all on a page that loaded with an empty cart, so the first item
   a customer added produced no visible change anywhere in the header.

   Listens on both event names because the theme's add-to-cart paths do not
   agree on one: the bundler and the you-may-be-interested-in section fire
   `cart:updated`, other places fire `cart:refresh`. */
(function () {
  function paint(count) {
    var el = document.querySelector('.cg-cart-count');
    if (!el) return;
    el.textContent = count;
    el.classList.toggle('cg-cart-count--empty', !count);
  }

  function refresh() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    fetch(root + 'cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) { if (cart) paint(cart.item_count); })
      .catch(function () { /* leave the badge as it is rather than showing a wrong number */ });
  }

  ['cart:updated', 'cart:refresh', 'cart:build'].forEach(function (name) {
    document.addEventListener(name, refresh);
    document.documentElement.addEventListener(name, refresh);
  });
})();
